class DistributorDirectory {
  constructor(section) {
    this.section = section;
    this.resultsEl = section.querySelector('[data-distributor-results]');
    this.emptyEl = section.querySelector('[data-distributor-empty]');
    this.countrySelect = section.querySelector('[data-distributor-country]');
    this.suggestionsEl = section.querySelector('[data-distributor-suggestions]');
    this.searchInput = section.querySelector('[data-distributor-search]');
    this.clearButton = section.querySelector('[data-distributor-clear]');
    this.defaultOpenRegion = (section.dataset.defaultOpenRegion || '').trim().toLowerCase();
    this.showAllSuggestions = false;

    const dataEl = section.querySelector('[data-distributor-data]');
    this.entries = this.parseData(dataEl);
    this.section.dataset.distributorParsedCount = String(this.entries.length);
    this.activeEntries = this.entries.filter((entry) => entry.is_active);

    this.allCountries = this.collectCountries();
    this.bindEvents();
    this.updateResults();
  }

  parseData(dataEl) {
    if (!dataEl) return [];
    try {
      const parsed = JSON.parse((dataEl.textContent || '[]').trim());
      const list = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed.items)
          ? parsed.items
          : [];
      return list.map((entry) => this.normalizeEntry(entry));
    } catch (error) {
      console.warn('Distributor directory: invalid JSON', error);
      this.section.dataset.distributorError = 'json-parse-failed';
      return [];
    }
  }

  normalizeEntry(entry) {
    const countries = this.toArray(entry.countries_served).map((value) => value.toUpperCase());
    return {
      company_name: this.toString(entry.company_name),
      region: this.toString(entry.region),
      type: this.toString(entry.type),
      countries_served: countries,
      address_lines: this.toString(entry.address_lines),
      country_base: this.toString(entry.country_base),
      phone: this.toString(entry.phone),
      email: this.toString(entry.email),
      website_url: this.toString(entry.website_url),
      notes: this.toString(entry.notes),
      sort_order: this.toNumber(entry.sort_order),
      is_active: this.toBoolean(entry.is_active)
    };
  }

  toArray(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    if (typeof value === 'string') {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
    return [];
  }

  toString(value) {
    return value == null ? '' : String(value).trim();
  }

  toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
  }

  toBoolean(value) {
    return value === true || value === 'true';
  }

  collectCountries() {
    const countrySet = new Set();
    this.activeEntries.forEach((entry) => {
      entry.countries_served.forEach((country) => countrySet.add(country));
    });
    return Array.from(countrySet).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }

  bindEvents() {
    if (this.countrySelect) {
      this.countrySelect.addEventListener('input', () => {
        this.showAllSuggestions = false;
        this.updateSuggestions();
        this.updateResults();
      });
      this.countrySelect.addEventListener('focus', () => {
        this.showAllSuggestions = true;
        this.updateSuggestions();
      });
      this.countrySelect.addEventListener('blur', () => {
        setTimeout(() => this.hideSuggestions(), 120);
      });
    }
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.updateResults());
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearFilters());
    }
  }

  clearFilters() {
    if (this.countrySelect) this.countrySelect.value = '';
    if (this.searchInput) this.searchInput.value = '';
    this.updateResults();
    this.hideSuggestions();
  }

  updateResults() {
    if (!this.resultsEl) return;

    const searchTerm = this.searchInput ? this.searchInput.value.trim().toLowerCase() : '';
    const selectedCountry = this.countrySelect ? this.countrySelect.value.trim() : '';

    const filtered = this.activeEntries.filter((entry) => {
      if (selectedCountry) {
        const selectedUpper = selectedCountry.toUpperCase();
        if (!entry.countries_served.includes(selectedUpper)) return false;
      }

      if (searchTerm) {
        const searchable = [
          entry.company_name,
          entry.region,
          entry.country_base,
          entry.countries_served.join(' ')
        ]
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(searchTerm)) return false;
      }

      return true;
    });

    filtered.sort((a, b) =>
      a.company_name.localeCompare(b.company_name, undefined, { sensitivity: 'base' })
    );

    this.renderGroups(filtered);
  }

  renderGroups(entries) {
    this.resultsEl.innerHTML = '';

    if (!entries.length) {
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }
    if (this.emptyEl) this.emptyEl.hidden = true;

    const grouped = new Map();
    entries.forEach((entry) => {
      const group = this.getRegionGroup(entry);
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group).push(entry);
    });

    const groupOrder = [
      'North America',
      'UK & Ireland',
      'Western Europe',
      'Nordics',
      'Central & Eastern Europe',
      'Australia & New Zealand',
      'South America',
      'Compliance',
      'Other'
    ];

    groupOrder.forEach((groupName) => {
      const groupEntries = grouped.get(groupName) || [];
      if (!groupEntries.length) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'distributor-directory__group';
      const title = document.createElement('h2');
      title.className = 'distributor-directory__group-title';
      title.textContent = groupName;
      const cards = document.createElement('div');
      cards.className = 'distributor-directory__cards';
      groupEntries.forEach((entry) => cards.appendChild(this.createCard(entry)));
      wrapper.append(title, cards);
      this.resultsEl.appendChild(wrapper);
    });
  }

  createCard(entry) {
    const card = document.createElement('article');
    card.className = 'distributor-directory__card';

    const title = document.createElement('h3');
    title.className = 'distributor-directory__company';
    title.textContent = entry.company_name;
    card.appendChild(title);

    if (entry.countries_served.length) {
      const chips = document.createElement('div');
      chips.className = 'distributor-directory__chips';
      entry.countries_served.forEach((country) => {
        const chip = document.createElement('span');
        chip.className = 'distributor-directory__chip callout-badge callout-badge--custom';
        chip.textContent = country;
        chips.appendChild(chip);
      });
      card.appendChild(chips);
    }

    if (entry.address_lines) {
      const address = document.createElement('div');
      address.className =
        'distributor-directory__meta distributor-directory__meta--top distributor-directory__address';
      address.innerHTML = `${this.iconMarkup('location')}<span>${this.escapeHtml(
        entry.address_lines
      ).replace(/\n/g, '<br>')}</span>`;
      card.appendChild(address);
    }

    if (entry.phone || entry.email) {
      const contact = document.createElement('div');
      contact.className = 'distributor-directory__contact';
      if (entry.phone) {
        const phoneLine = document.createElement('div');
        phoneLine.className = 'distributor-directory__meta';
        phoneLine.innerHTML = `${this.iconMarkup('phone')}<a href="tel:${entry.phone.replace(
          /[^+\\d]/g,
          ''
        )}">${this.escapeHtml(entry.phone)}</a>`;
        contact.appendChild(phoneLine);
      }
      if (entry.email) {
        const emailLine = document.createElement('div');
        emailLine.className = 'distributor-directory__meta';
        emailLine.innerHTML = `${this.iconMarkup('email')}<a href="mailto:${this.escapeHtml(
          entry.email
        )}">${this.escapeHtml(entry.email)}</a>`;
        contact.appendChild(emailLine);
      }
      card.appendChild(contact);
    }

    const actions = document.createElement('div');
    actions.className = 'distributor-directory__actions';
    if (entry.website_url) {
      const websiteButton = document.createElement('a');
      websiteButton.className = 'button button--primary distributor-directory__button';
      websiteButton.href = entry.website_url;
      websiteButton.target = '_blank';
      websiteButton.rel = 'noopener';
      websiteButton.textContent = 'Visit website';
      actions.appendChild(websiteButton);
    } else if (entry.email) {
      const emailButton = document.createElement('a');
      emailButton.className = 'button button--primary distributor-directory__button';
      emailButton.href = `mailto:${entry.email}`;
      emailButton.textContent = 'Email';
      actions.appendChild(emailButton);
    } else if (entry.phone) {
      const callButton = document.createElement('a');
      callButton.className = 'button button--primary distributor-directory__button';
      callButton.href = `tel:${entry.phone.replace(/[^+\\d]/g, '')}`;
      callButton.textContent = 'Call';
      actions.appendChild(callButton);
    }
    if (actions.childElementCount) {
      card.appendChild(actions);
    }

    if (entry.notes) {
      const notes = document.createElement('p');
      notes.className = 'distributor-directory__notes';
      notes.textContent = entry.notes;
      card.appendChild(notes);
    }

    return card;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  iconMarkup(type) {
    if (type === 'phone') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6.6 10.8c1.6 3.1 3.5 5 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.4 21 3 13.6 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1.1L6.6 10.8z" />
        </svg>
      `;
    }
    if (type === 'email') {
      return `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      `;
    }
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z" />
      </svg>
    `;
  }

  updateSuggestions() {
    if (!this.suggestionsEl || !this.countrySelect) return;
    const query = this.countrySelect.value.trim().toLowerCase();
    const filtered = this.showAllSuggestions
      ? this.allCountries
      : this.allCountries.filter((country) => country.toLowerCase().includes(query));
    const limited = filtered;
    this.suggestionsEl.innerHTML = '';

    const allItem = document.createElement('button');
    allItem.type = 'button';
    allItem.className = 'distributor-directory__typeahead-item';
    allItem.textContent = 'All countries';
    allItem.addEventListener('click', () => this.selectCountry(''));
    this.suggestionsEl.appendChild(allItem);

    limited.forEach((country) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'distributor-directory__typeahead-item';
      item.textContent = country;
      item.addEventListener('click', () => this.selectCountry(country));
      this.suggestionsEl.appendChild(item);
    });

    this.suggestionsEl.hidden = false;
  }

  hideSuggestions() {
    if (this.suggestionsEl) {
      this.suggestionsEl.hidden = true;
    }
  }

  selectCountry(country) {
    if (!this.countrySelect) return;
    this.countrySelect.value = country;
    this.showAllSuggestions = true;
    this.updateResults();
    this.hideSuggestions();
  }

  getRegionGroup(entry) {
    if (entry.type === 'Authorised representative') return 'Compliance';
    const reference = (entry.country_base || entry.countries_served[0] || '').toLowerCase();
    const westernEurope = [
      'france',
      'germany',
      'netherlands',
      'belgium',
      'luxembourg',
      'austria',
      'switzerland',
      'spain',
      'portugal',
      'italy',
      'greece',
      'cyprus'
    ];
    const centralEasternEurope = [
      'bulgaria',
      'czech republic',
      'romania',
      'slovakia',
      'slovenia',
      'poland',
      'hungary'
    ];
    const northAmerica = ['usa', 'united states', 'canada', 'mexico'];
    const ukIreland = ['united kingdom', 'uk', 'ireland'];
    const nordics = ['norway', 'sweden', 'denmark', 'finland', 'iceland'];
    const aunz = ['australia', 'new zealand'];
    const southAmerica = ['chile', 'peru'];

    if (northAmerica.includes(reference)) return 'North America';
    if (ukIreland.includes(reference)) return 'UK & Ireland';
    if (nordics.includes(reference)) return 'Nordics';
    if (aunz.includes(reference)) return 'Australia & New Zealand';
    if (southAmerica.includes(reference)) return 'South America';
    if (westernEurope.includes(reference)) return 'Western Europe';
    if (centralEasternEurope.includes(reference)) return 'Central & Eastern Europe';
    return 'Other';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('[data-distributor-section]')
    .forEach((section) => new DistributorDirectory(section));
});
